import { flatMap, isFunction } from 'lodash';
import { CheckIcon } from 'lucide-react';
import { ButtonHTMLAttributes, forwardRef, useState } from 'react';

import { Popover, PopoverContent, PopoverTrigger } from '@/components/popover';
import { ScrollArea } from '@/components/scroll-area';
import { ComboboxMultiTrigger } from '@/components/ui/inputs/combobox-multi/trigger';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/inputs/command';
import { OptionGroup } from '@/types';
import { tv } from '@/utils/styles';

export interface ComboboxMultiProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /**
   * An array of OptionGroup objects to be displayed in the multi-select component.
   */
  options: OptionGroup[];

  /**
   * Callback function triggered when the selected values change.
   * Receives an array of the new selected values.
   */
  onValueChange: (value: string[]) => void;

  /** The currently selected values */
  selectedValues: string[];

  /**
   * Placeholder text to be displayed when no values are selected.
   * Optional, defaults to "Select options".
   */
  placeholder?: string;

  /**
   * Maximum number of selected items to display when using tag trigger. Extra
   * selected items will be summarised.
   * Optional, defaults to 3.
   */
  maxCount?: number;

  /**
   * The modality of the popover. When set to true, interaction with outside elements
   * will be disabled and only popover content will be visible to screen readers.
   * Optional, defaults to false.
   */
  modalPopover?: boolean;

  /**
   * Additional class names to apply custom styles to the multi-select component.
   * Optional, can be used to add custom styles.
   */
  className?: string;

  /**
   * Callback function triggered when a user clicks create.
   * Optional, will not render create item if undefined
   */
  onCreateNewItem?: (value: string) => void;

  /**
   * Initial value for isPopoverOpen
   * Optional, default to false
   */
  isOpen?: boolean;

  /**
   * Initial value for searchValue
   * Optional, defaults to empty string
   */
  searchValue?: string;

  /**
   * Control showing a select all option or not
   * Optional, defaults to false
   */
  shouldShowSelectAll?: boolean;
}

const ComboboxMulti = forwardRef<HTMLButtonElement, ComboboxMultiProps>(
  (
    {
      options,
      onValueChange,
      selectedValues,
      placeholder = 'Select options',
      maxCount,
      modalPopover = false,
      className,
      onCreateNewItem,
      isOpen = false,
      searchValue: searchValueProp = '',
      shouldShowSelectAll = false,
    },
    ref,
  ) => {
    const [searchValue, setSearchValue] = useState(searchValueProp);
    const [isPopoverOpen, setIsPopoverOpen] = useState(isOpen);

    const flatOptions = flatMap(options, 'items');
    const handleInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        setIsPopoverOpen(true);
      } else if (event.key === 'Backspace' && !event.currentTarget.value) {
        const newSelectedValues = [...selectedValues];
        newSelectedValues.pop();
        onValueChange(newSelectedValues);
      }
    };

    const toggleOption = (value: string) => {
      const newSelectedValues = selectedValues.includes(value)
        ? selectedValues.filter((v) => v !== value)
        : [...selectedValues, value];
      onValueChange(newSelectedValues);
    };

    const handleClear = () => {
      onValueChange([]);
      setIsPopoverOpen(false);
    };

    const handleTogglePopover = () => {
      setIsPopoverOpen((prev) => !prev);
    };

    const toggleAll = () => {
      if (selectedValues.length === flatOptions.length) {
        handleClear();
      } else {
        const allValues = flatOptions.map((option) => option.value);
        onValueChange(allValues);
      }
    };

    const handleCreateAndSelectNewItem = (value: string) => {
      if (isFunction(onCreateNewItem)) {
        onCreateNewItem(value);
        toggleOption(value);
      }
    };

    const { icon, popoverContent, checkWrapper, commandItem } = styles();

    return (
      <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen} modal={modalPopover}>
        <PopoverTrigger asChild>
          <ComboboxMultiTrigger
            selectedValues={selectedValues}
            onTogglePopover={handleTogglePopover}
            onValueChange={onValueChange}
            maxCount={maxCount}
            options={flatOptions}
            placeholder={placeholder}
            className={className}
            isOpen={isPopoverOpen}
            ref={ref}
          />
        </PopoverTrigger>
        <PopoverContent className={popoverContent()} align="start" onEscapeKeyDown={() => setIsPopoverOpen(false)}>
          <Command className="min-w-none">
            <CommandInput
              placeholder="Search..."
              onKeyDown={handleInputKeyDown}
              value={searchValue}
              onValueChange={setSearchValue}
            />
            <CommandList>
              <CommandEmpty>No results found</CommandEmpty>
              <ScrollArea>
                <CommandGroup>
                  {shouldShowSelectAll ? (
                    <CommandItem key="all" onSelect={toggleAll} className={commandItem()} value="Select all">
                      <div className={checkWrapper({ isSelected: selectedValues.length === flatOptions.length })}>
                        <CheckIcon className={icon()} />
                      </div>
                      <span>Select All</span>
                    </CommandItem>
                  ) : null}
                  {flatOptions.map((option) => {
                    const isSelected = selectedValues.includes(option.value);
                    return (
                      <CommandItem
                        key={option.value}
                        onSelect={() => toggleOption(option.value)}
                        className={commandItem()}
                        value={option.label}
                      >
                        <div className={checkWrapper({ isSelected })}>
                          <CheckIcon className={icon()} />
                        </div>
                        <span>{option.label}</span>
                      </CommandItem>
                    );
                  })}
                  {isFunction(onCreateNewItem) && searchValue && (
                    <CommandItem
                      key="create"
                      onSelect={() => handleCreateAndSelectNewItem(searchValue)}
                      className={commandItem()}
                    >
                      Create &quot;{searchValue}&quot;
                    </CommandItem>
                  )}
                </CommandGroup>
              </ScrollArea>
              {selectedValues.length > 0 && (
                <>
                  <CommandSeparator className="m-0" />
                  <CommandGroup>
                    <CommandItem onSelect={handleClear} className={commandItem({ className: 'justify-center' })}>
                      Clear filters
                    </CommandItem>
                  </CommandGroup>
                </>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    );
  },
);

ComboboxMulti.displayName = 'ComboboxMulti';

const styles = tv({
  slots: {
    icon: 'size-4',
    popoverContent: 'w-auto p-0',
    checkWrapper: 'flex size-4 items-center justify-center rounded border-2 border-solid border-grey-300 bg-white',
    commandItem: 'flex cursor-pointer gap-2',
  },
  variants: {
    isSelected: {
      true: {
        checkWrapper: 'border-lightBlue-500 bg-lightBlue-500 text-white',
      },
      false: {
        checkWrapper: 'opacity-50 [&_svg]:invisible',
      },
    },
  },
});

export { ComboboxMulti };
